import { useMemo } from 'react';
import { Link, useParams } from 'react-router';
import { Loader2, ArrowLeft, Download, FileText, Eye, Clock, Share2, ListOrdered } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/landing/Footer';
import Seo from '../../components/Seo';
import { useGetBlogBySlugQuery } from '../../store/api/apiSlice';
import { resolveFileUrl } from '../../utils/fileUrl';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { blogBreadcrumbJsonLd, blogPostingJsonLd } from '../../utils/blogSeo';
import { getRepoPost, repoPosts, type RepoPost } from '../../content/blog';

const CALENDAR_URL =
    'https://calendar.google.com/calendar/appointments/schedules/AcZssZ1X55XupDolgAEHQOxuiyB8bdyB9hO53uywaJpR60NtMsnMHgAoDlVXD-jzYrNA2z-0z0NIbXvf?gv=true';

const SITE_URL = 'https://checker.podus.org';

// Dark editorial hero backdrop: brand-tinted glows + a faint dot grid.
const HERO_STYLE: React.CSSProperties = {
    backgroundColor: '#0f1115',
    backgroundImage:
        'radial-gradient(60rem 30rem at 12% -10%, rgba(255,107,53,0.22), transparent 55%),' +
        'radial-gradient(50rem 30rem at 100% 120%, rgba(255,107,53,0.12), transparent 50%),' +
        'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
    backgroundSize: 'auto, auto, 22px 22px',
};

function slugifyHeading(text: string): string {
    return (
        text
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-')
            .slice(0, 60) || 'section'
    );
}

// Add stable ids to the article's H2s and return a table of contents built from
// them, so the sidebar can offer in-page navigation.
function withHeadingAnchors(html: string): { html: string; toc: { id: string; text: string }[] } {
    const toc: { id: string; text: string }[] = [];
    const used = new Set<string>();
    const out = html.replace(/<h2>([\s\S]*?)<\/h2>/g, (_m, inner: string) => {
        const text = inner.replace(/<[^>]+>/g, '').trim();
        let id = slugifyHeading(text);
        let n = 2;
        while (used.has(id)) id = `${slugifyHeading(text)}-${n++}`;
        used.add(id);
        toc.push({ id, text });
        return `<h2 id="${id}">${inner}</h2>`;
    });
    return { html: out, toc };
}

function shareLinks(title: string, url: string) {
    const u = encodeURIComponent(url);
    const t = encodeURIComponent(title);
    return {
        x: `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
        whatsapp: `https://wa.me/?text=${t}%20${u}`,
    };
}

function formatDate(iso: string | null): string {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' });
}

function authorName(author: { firstName?: string; lastName?: string } | null): string {
    if (!author) return 'YourChecka';
    const name = `${author.firstName ?? ''} ${author.lastName ?? ''}`.trim();
    return name || 'YourChecka';
}

export default function BlogDetail() {
    const { slug } = useParams<{ slug: string }>();

    // Posts that live in the repo render instantly from the bundle and are the
    // ones prerendered to static HTML for search engines. Anything else falls
    // through to the backend CMS.
    const repoPost = useMemo(() => getRepoPost(slug), [slug]);

    const { data: post, isLoading, isError } = useGetBlogBySlugQuery(slug ?? '', { skip: !slug || !!repoPost });

    const cleanBody = useMemo(() => sanitizeHtml(post?.body), [post?.body]);
    const openScheduler = () => window.open(CALENDAR_URL, '_blank', 'noopener,noreferrer');
    const cover = resolveFileUrl(post?.coverImageUrl);
    const docUrl = resolveFileUrl(post?.documentUrl);

    if (repoPost) {
        return <RepoArticle slug={repoPost.slug} onBookMeeting={openScheduler} />;
    }

    return (
        <div className="flex min-h-screen flex-col bg-white">
            <Header />
            <main className="flex-1">
                {isLoading ? (
                    <div className="flex items-center justify-center py-32">
                        <Loader2 className="h-8 w-8 animate-spin text-[#FF6B35]" />
                    </div>
                ) : isError || !post ? (
                    <div className="py-32 text-center">
                        <FileText className="mx-auto mb-4 h-12 w-12 text-gray-300" />
                        <h1 className="text-xl font-bold text-gray-900">Article not found</h1>
                        <p className="mt-2 text-gray-500">This post may have been removed or is not yet published.</p>
                        <Link
                            to="/blog"
                            className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-black px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-900"
                        >
                            <ArrowLeft className="h-4 w-4" /> Back to blog
                        </Link>
                    </div>
                ) : (
                    <article className="mx-auto w-full max-w-[46rem] px-4 py-8 md:px-6 md:py-12">
                        <Seo
                            title={`${post.title} | YourChecka`}
                            description={post.excerpt ?? post.title}
                            canonicalPath={`/blog/${post.slug}`}
                            image={post.coverImageUrl ?? undefined}
                            type="article"
                            jsonLd={blogPostingJsonLd({
                                slug: post.slug,
                                title: post.title,
                                description: post.excerpt ?? post.title,
                                date: post.publishedAt,
                                author: authorName(post.author),
                                coverImageUrl: post.coverImageUrl,
                            })}
                        />
                        <Link
                            to="/blog"
                            className="mb-8 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-[#FF6B35]"
                        >
                            <ArrowLeft className="h-4 w-4" /> All articles
                        </Link>

                        <h1 className="text-3xl font-bold leading-tight text-gray-900 md:text-4xl">{post.title}</h1>

                        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                            <span>By {authorName(post.author)}</span>
                            {post.publishedAt && (
                                <>
                                    <span className="text-gray-300">•</span>
                                    <span>{formatDate(post.publishedAt)}</span>
                                </>
                            )}
                            <span className="text-gray-300">•</span>
                            <span className="inline-flex items-center gap-1">
                                <Eye className="h-3.5 w-3.5" /> {post.viewCount} views
                            </span>
                        </div>

                        {cover && (
                            <img
                                src={cover}
                                alt={post.title}
                                className="mt-8 aspect-[16/9] w-full rounded-2xl object-cover"
                            />
                        )}

                        {post.excerpt && (
                            <p className="mt-8 border-l-4 border-[#FF6B35]/40 pl-4 text-lg italic text-gray-600">
                                {post.excerpt}
                            </p>
                        )}

                        {post.contentType === 'document' ? (
                            <div className="mt-8 rounded-2xl border border-gray-100 bg-gray-50 p-6">
                                <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF6B35]/10">
                                            <FileText className="h-5 w-5 text-[#FF6B35]" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-900">
                                                {post.documentName ?? 'Download document'}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {post.documentMimeType ?? 'Attached file'}
                                            </p>
                                        </div>
                                    </div>
                                    {docUrl && (
                                        <a
                                            href={docUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            download={post.documentName ?? undefined}
                                            className="inline-flex items-center gap-1.5 rounded-full bg-[#FF6B35] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#ff5722]"
                                        >
                                            <Download className="h-4 w-4" /> Download
                                        </a>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div
                                className="blog-body mt-8 max-w-none text-gray-800"
                                // Body is authored by Super Admins and sanitized in sanitizeHtml().
                                dangerouslySetInnerHTML={{ __html: cleanBody }}
                            />
                        )}

                        {/* Tags — shown at the end of the article. */}
                        {post.tags && post.tags.length > 0 && (
                            <div className="mt-10 flex flex-wrap gap-2 border-t border-gray-100 pt-6">
                                {post.tags.map((tag) => (
                                    <span
                                        key={tag}
                                        className="inline-flex items-center rounded-full bg-[#FF6B35]/10 px-3 py-1 text-xs font-medium text-[#FF6B35]"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        )}
                    </article>
                )}
            </main>
            <Footer onBookMeeting={openScheduler} />
        </div>
    );
}

/**
 * Article rendered from a Markdown file in `src/content/blog`. No API call, so
 * it paints immediately and can be prerendered to static HTML at build time.
 */
function RepoArticle({ slug, onBookMeeting }: { slug: string; onBookMeeting: () => void }) {
    const post = getRepoPost(slug)!;

    // Simple internal linking: the next few articles, which helps crawlers find
    // every post and keeps readers on the site.
    const related = repoPosts.filter((p) => p.slug !== post.slug).slice(0, 4);

    const { html: bodyHtml, toc } = useMemo(() => withHeadingAnchors(post.html), [post.html]);
    const shareUrl = `${SITE_URL}/blog/${post.slug}`;
    const share = shareLinks(post.title, shareUrl);

    const tocNav =
        toc.length > 1 ? (
            <nav aria-label="On this page">
                <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                    <ListOrdered className="h-3.5 w-3.5" /> On this page
                </p>
                <ul className="flex flex-col gap-2 border-l border-gray-200 pl-4">
                    {toc.map((item) => (
                        <li key={item.id}>
                            <a
                                href={`#${item.id}`}
                                className="block text-sm leading-snug text-gray-500 transition-colors hover:text-[#FF6B35]"
                            >
                                {item.text}
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>
        ) : null;

    const shareBlock = (
        <div>
            <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                <Share2 className="h-3.5 w-3.5" /> Share
            </p>
            <div className="flex flex-wrap gap-2">
                <ShareLink href={share.whatsapp} label="Share on WhatsApp">
                    WhatsApp
                </ShareLink>
                <ShareLink href={share.x} label="Share on X">
                    X
                </ShareLink>
                <ShareLink href={share.linkedin} label="Share on LinkedIn">
                    LinkedIn
                </ShareLink>
            </div>
        </div>
    );

    return (
        <div className="flex min-h-screen flex-col bg-white">
            <Header />
            <Seo
                title={`${post.title} | YourChecka`}
                description={post.description}
                canonicalPath={`/blog/${post.slug}`}
                image={post.coverImageUrl}
                type="article"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [blogPostingJsonLd(post), blogBreadcrumbJsonLd(post.title, post.slug)],
                }}
            />

            <main className="flex-1">
                {/* Full-width hero band */}
                <header className="relative overflow-hidden text-white" style={HERO_STYLE}>
                    <div className="mx-auto w-full max-w-[100rem] px-4 py-12 md:px-6 md:py-16 lg:px-10 lg:py-20">
                        <Link
                            to="/blog"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-white/60 transition-colors hover:text-white"
                        >
                            <ArrowLeft className="h-4 w-4" /> All articles
                        </Link>

                        {post.tags[0] && (
                            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.15em] text-[#FF6B35]">
                                {post.tags[0]}
                            </p>
                        )}

                        <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-[1.15] tracking-tight md:text-4xl lg:text-5xl">
                            {post.title}
                        </h1>

                        {post.excerpt && (
                            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
                                {post.excerpt}
                            </p>
                        )}

                        <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/60">
                            <span className="inline-flex items-center gap-2">
                                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#FF6B35]/20 text-[11px] font-bold text-[#FF6B35]">
                                    YC
                                </span>
                                By {post.author}
                            </span>
                            {post.date && (
                                <time dateTime={post.date} className="inline-flex items-center gap-1.5">
                                    {formatDate(post.date)}
                                </time>
                            )}
                            <span className="inline-flex items-center gap-1.5">
                                <Clock className="h-3.5 w-3.5" /> {post.readingMinutes} min read
                            </span>
                        </div>
                    </div>
                </header>

                {post.coverImageUrl && (
                    <div className="mx-auto w-full max-w-5xl px-4 md:px-6">
                        <img
                            src={post.coverImageUrl}
                            alt={post.title}
                            className="-mt-8 aspect-[16/9] w-full rounded-2xl border border-black/5 object-cover shadow-xl md:-mt-10"
                        />
                    </div>
                )}

                {/* Three-rail body: share · centered article · contents. Rails
                    fill the full width while the reading column stays readable. */}
                <div className="mx-auto grid w-full max-w-[100rem] grid-cols-1 gap-x-12 gap-y-8 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-[minmax(0,1fr)_15rem] lg:px-10 xl:grid-cols-[minmax(0,1fr)_minmax(0,46rem)_minmax(0,1fr)]">
                    {/* Left rail (xl+): share */}
                    <aside className="hidden xl:block xl:justify-self-end">
                        <div className="sticky top-24 w-44">{shareBlock}</div>
                    </aside>

                    <article className="min-w-0 max-w-[46rem] xl:justify-self-center">
                        <div
                            className="blog-body max-w-none text-gray-800 [&_h2]:scroll-mt-24"
                            // Compiled from our own Markdown files in the repo, not user input.
                            dangerouslySetInnerHTML={{ __html: bodyHtml }}
                        />

                        {/* Tags close out the article body. */}
                        {post.tags.length > 0 && (
                            <div className="mt-10 flex flex-wrap gap-2 border-t border-gray-100 pt-6">
                                {post.tags.map((tag) => (
                                    <span
                                        key={tag}
                                        className="inline-flex items-center rounded-full bg-[#FF6B35]/10 px-3 py-1 text-xs font-medium text-[#FF6B35]"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        )}
                    </article>

                    {/* Right rail: contents (lg+); share also shown here until the
                        left rail takes over at xl. */}
                    <aside className="hidden lg:block xl:justify-self-start">
                        <div className="sticky top-24 flex w-full max-w-[15rem] flex-col gap-8">
                            {tocNav}
                            <div className="xl:hidden">{shareBlock}</div>
                        </div>
                    </aside>
                </div>

                {/* Keep reading — full-width card grid fills the space below. */}
                {related.length > 0 && (
                    <section className="border-t border-gray-100 bg-[#F7F7F8]">
                        <div className="mx-auto w-full max-w-[100rem] px-4 py-12 md:px-6 md:py-16 lg:px-10">
                            <h2 className="text-xl font-bold text-gray-900 md:text-2xl">Keep reading</h2>
                            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                                {related.map((r) => (
                                    <RelatedCard key={r.slug} post={r} />
                                ))}
                            </div>
                        </div>
                    </section>
                )}
            </main>
            <Footer onBookMeeting={onBookMeeting} />
        </div>
    );
}

function ShareLink({ href, label, children }: { href: string; label: string; children: React.ReactNode }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-[#FF6B35] hover:text-[#FF6B35]"
        >
            {children}
        </a>
    );
}

function RelatedCard({ post }: { post: RepoPost }) {
    return (
        <Link
            to={`/blog/${post.slug}`}
            className="group flex flex-col rounded-2xl border border-gray-100 bg-white p-5 transition-shadow hover:shadow-md"
        >
            {post.tags[0] && (
                <span className="mb-3 w-fit rounded-full bg-[#FF6B35]/10 px-2.5 py-0.5 text-[11px] font-medium text-[#FF6B35]">
                    {post.tags[0]}
                </span>
            )}
            <h3 className="font-bold leading-snug text-gray-900 transition-colors group-hover:text-[#FF6B35]">
                {post.title}
            </h3>
            {post.excerpt && <p className="mt-2 line-clamp-3 text-sm text-gray-600">{post.excerpt}</p>}
            <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                <Clock className="h-3 w-3" /> {post.readingMinutes} min read
            </span>
        </Link>
    );
}
